import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, Compass, CreditCard, ExternalLink, Download, FileDown, XCircle } from "lucide-react";

export default function MemberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([api.get("/subscriptions/mine"), api.get("/purchases/mine")]);
      setSubs(s.data);
      setPurchases(p.data);
    } catch (e) {
      toast.error("Failed to load your dashboard.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const activeSubs = subs.filter((s) => s.status === "active");
  const monthly = activeSubs.reduce((a, s) => a + s.amount, 0);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/subscriptions/${cancelTarget.id}/cancel`);
      toast.success("Subscription cancelled.");
      setCancelTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel.");
    } finally {
      setCancelling(false);
    }
  };

  const download = async (productId, filename) => {
    setDownloading(productId);
    try {
      const res = await api.get(`/products/${productId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  const statusPill = (status) => {
    const map = { active: "bg-lime-300", cancelled: "bg-rose-300", pending: "bg-amber-300" };
    return <span className={`chip ${map[status] || "bg-slate-200"}`}>{status}</span>;
  };

  if (loading) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="w-8 h-8 text-violet-600 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#111114]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="eyebrow text-violet-600 mb-1">Subscriber Portal</p>
        <h1 className="font-display text-3xl font-extrabold mb-8">Welcome, {user?.name} 👋</h1>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          <div className="card-pop bg-lime-200 p-6" data-testid="stat-active-subs">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5" /><span className="eyebrow">Active memberships</span></div>
            <p className="font-display text-3xl font-extrabold">{activeSubs.length}</p>
          </div>
          <div className="card-pop bg-cyan-200 p-6" data-testid="stat-monthly-spend">
            <div className="flex items-center gap-2 mb-2"><CreditCard className="w-5 h-5" /><span className="eyebrow">Monthly spend</span></div>
            <p className="font-display text-3xl font-extrabold">{money(monthly, activeSubs[0]?.currency)}</p>
          </div>
          <div className="card-pop bg-violet-200 p-6" data-testid="stat-downloads">
            <div className="flex items-center gap-2 mb-2"><Download className="w-5 h-5" /><span className="eyebrow">Downloads owned</span></div>
            <p className="font-display text-3xl font-extrabold">{purchases.length}</p>
          </div>
        </div>

        <Tabs defaultValue="subs">
          <TabsList className="bg-white border-2 border-[#111114] rounded-xl p-1 mb-6 h-auto">
            <TabsTrigger value="subs" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold" data-testid="tab-subs">Memberships</TabsTrigger>
            <TabsTrigger value="downloads" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold" data-testid="tab-downloads">My downloads</TabsTrigger>
          </TabsList>

          <TabsContent value="subs">
            {subs.length === 0 ? (
              <div className="card-pop bg-white p-12 text-center" data-testid="empty-subs">
                <Compass className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-6">You're not subscribed to any creators yet.</p>
                <Button onClick={() => navigate("/")} className="bg-violet-600 text-white btn-pop rounded-full font-bold">Explore creators</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {subs.map((s) => (
                  <div key={s.id} className="card-pop bg-white p-5 flex items-center justify-between gap-4" data-testid={`member-sub-${s.id}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-xl border-2 border-[#111114] bg-violet-200 flex items-center justify-center shrink-0 overflow-hidden">
                        {s.creator_avatar ? <img src={s.creator_avatar} alt={s.creator_name} className="w-full h-full object-cover" /> : <span className="font-display font-bold">{(s.creator_name || "?")[0]}</span>}
                      </div>
                      <div className="min-w-0">
                        <p className="font-display font-bold truncate">{s.creator_name}</p>
                        <p className="text-slate-500 text-sm">{s.tier_name} · {money(s.amount, s.currency)}/mo</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {statusPill(s.status)}
                      {s.status === "active" && (
                        <Button onClick={() => setCancelTarget(s)} className="bg-white text-[#111114] btn-pop rounded-full h-9 text-sm font-bold hover:bg-rose-50" data-testid={`cancel-btn-${s.id}`}>
                          Manage
                        </Button>
                      )}
                      {s.creator_handle && (
                        <a href={`/s/${s.creator_handle}`} className="w-9 h-9 rounded-lg border-2 border-[#111114] bg-white flex items-center justify-center hover:bg-violet-100" data-testid={`visit-creator-${s.id}`}>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="downloads">
            {purchases.length === 0 ? (
              <div className="card-pop bg-white p-12 text-center" data-testid="empty-downloads">
                <FileDown className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600">No purchases yet. Grab a digital product from a creator's storefront.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {purchases.map((p) => (
                  <div key={p.id} className="card-pop bg-white overflow-hidden flex flex-col" data-testid={`purchase-${p.id}`}>
                    <div className="h-32 bg-cover bg-center border-b-2 border-[#111114] relative" style={{ backgroundImage: `url(${p.thumbnail_url || "https://images.unsplash.com/photo-1554056878-6911635b1573?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"})` }}>
                      <span className="chip bg-lime-300 absolute top-3 left-3">{p.file_type}</span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-display font-extrabold">{p.product_name}</h3>
                      <p className="text-slate-500 text-xs mb-4">by {p.creator_name}</p>
                      <Button onClick={() => download(p.product_id, p.original_filename)} disabled={downloading === p.product_id}
                        className="mt-auto w-full bg-violet-600 text-white btn-pop rounded-full font-bold hover:bg-violet-500" data-testid={`download-btn-${p.product_id}`}>
                        {downloading === p.product_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Download</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="bg-white border-2 border-[#111114] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><XCircle className="w-5 h-5 text-rose-500" /> Cancel membership?</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 text-sm">
            You're about to cancel <span className="font-bold text-[#111114]">{cancelTarget?.tier_name}</span> from <span className="font-bold text-[#111114]">{cancelTarget?.creator_name}</span>.
            Your access ends immediately and billing stops.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setCancelTarget(null)} className="bg-white text-[#111114] btn-pop rounded-full font-bold" data-testid="cancel-dialog-keep">Keep it</Button>
            <Button onClick={doCancel} disabled={cancelling} className="bg-rose-500 text-white btn-pop rounded-full font-bold hover:bg-rose-400" data-testid="cancel-dialog-confirm">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel subscription"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
